import type {
  IEntityTimesSettings,
  IProjectTaskListSetting,
  IProjectTimeTrackingTrackedPlannedSetting,
  IProjectViewSetting,
  ITaskViewListsSetting,
  IViewPersistenceSetting
} from '@awork/framework/models/view-setting.model'
import { DEFAULT_FRAMEWORK_VIEW_SETTINGS } from '@awork/framework/state/settings.query'
import { Injectable } from '@angular/core'
import { SignalStore } from '@awork/core/state/signal-store/signalStore'
import type { UserSetting } from '@awork/_shared/models/user-setting.model'
import { defaultPlannerCalendarControlsSettings } from '@awork/_shared/models/planner-calendar-controls.settings.model'
import { defaultOnboardingSettings } from '@awork/features/onboarding/models/onboarding-settings.model'
import type { EntityName } from '@awork/_shared/services/user-settings-service/user-settings.service'
import { defaultaworkAIUserPageSettings, defaultUserPageSettings } from '@awork/features/user/state/types'
import { defaultCompanyPageSettings } from '@awork/features/company/state/types'
import {
  defaultaworkAIProjectPageSettings,
  defaultProjectPageSettings
} from '@awork/features/project/types/project-page-settings'
import type {
  EntitySettingsKey,
  EntitySettingsValue,
  IGroupingSorting,
  NotificationUserSetting,
  SettingsState
} from '@awork/framework/state/settings.types'

/**
 * Creates the default user settings for both products.
 * @returns The initial settings state.
 */
function createInitialState(): SettingsState {
  return {
    notifications: [],
    viewSettings: {},
    projectTaskListSettings: [],
    projectTaskList: [],
    projectTimeTrackingTrackedPlannedSettings: [],
    taskViewListsSettings: [],
    taskListWidgetSettings: [],
    taskFilterSidebarSettings: {},
    projectSettings: [],
    entityTimesSettings: [],
    hideTimers: false,
    sortingGrouping: {},
    documents: {},
    projectPage: defaultProjectPageSettings,
    aworkAIProjectPage: defaultaworkAIProjectPageSettings,
    userPage: defaultUserPageSettings,
    aworkAIUserPage: defaultaworkAIUserPageSettings,
    aworkAIProjectDocs: {},
    companyPage: defaultCompanyPageSettings,
    onboarding: defaultOnboardingSettings,
    plannerCalendarControlsSettings: defaultPlannerCalendarControlsSettings,
    desktopApp: null,
    commands: [],
    agentChat: {},
    agentUsageActivity: {},
    agentLibrary: {},
    entitySelectors: {}
  }
}

@Injectable({ providedIn: 'root' })
export class SettingsStore extends SignalStore<SettingsState> {
  constructor() {
    super({ name: 'settings', initialState: createInitialState() })
  }

  /**
   * Sets localStorage key 'awDarkMode' used to show the App's framework loader with the proper theme
   * @param {Readonly<SettingsState>} prevState
   * @param {Readonly<Partial<SettingsState>>} nextState
   * @returns {Partial<SettingsState>}
   */
  preUpdateState(
    prevState: Readonly<SettingsState>,
    nextState: Readonly<Partial<SettingsState>>
  ): Partial<SettingsState> {
    localStorage.setItem('awDarkMode', nextState.viewSettings?.framework?.darkMode || 'auto')
    localStorage.setItem('awMenu', nextState.viewSettings?.framework?.menuPinned ? 'true' : 'false')
    localStorage.setItem('awMenuCompact', nextState.viewSettings?.framework?.menuCompact ? 'true' : 'false')
    localStorage.setItem(
      'awMenuWidth',
      nextState.viewSettings?.framework?.menuWidth?.toString() || DEFAULT_FRAMEWORK_VIEW_SETTINGS.menuWidth.toString()
    )

    return nextState
  }

  /**
   * Updates the notification settings in the store
   * @param {NotificationUserSetting[]} notificationSettings
   */
  updateNotificationSettings(notificationSettings: NotificationUserSetting[]): void {
    this.update({
      notifications: notificationSettings
    })
  }

  /**
   * Updates the view setting in the store
   * @param {IViewPersistenceSetting} viewSetting
   */
  updateViewSettings(viewSetting: IViewPersistenceSetting): void {
    this.applyMigrations(viewSetting)

    this.update({ viewSettings: viewSetting })
  }

  /**
   * Updates the view setting in the store
   * @param {IProjectTaskListSetting} viewSetting
   */
  updateProjectTaskListViewSettings(viewSetting: IProjectTaskListSetting): void {
    this.update(state => {
      const projectTaskListSettings = state.projectTaskListSettings
        ? state.projectTaskListSettings.filter(setting => setting.projectId !== viewSetting.projectId)
        : []

      if (viewSetting.projectId) {
        projectTaskListSettings.push(viewSetting)
      }

      return { projectTaskListSettings }
    })
  }

  /**
   * Updates the time tracking tracked/planned per-project setting in the store
   * @param {IProjectTimeTrackingTrackedPlannedSetting} viewSetting
   */
  updateProjectTimeTrackingTrackedPlannedSettings(viewSetting: IProjectTimeTrackingTrackedPlannedSetting): void {
    this.update(state => {
      const projectTimeTrackingTrackedPlannedSettings = state.projectTimeTrackingTrackedPlannedSettings
        ? state.projectTimeTrackingTrackedPlannedSettings.filter(setting => setting.projectId !== viewSetting.projectId)
        : []

      if (viewSetting.projectId) {
        projectTimeTrackingTrackedPlannedSettings.push(viewSetting)
      }

      return { projectTimeTrackingTrackedPlannedSettings }
    })
  }

  /**
   * Updates the view setting in the store
   * @param {IProjectViewSetting} viewSetting
   */
  updateProjectViewSettings(viewSetting: IProjectViewSetting): void {
    this.update(state => {
      const projectSettings = state.projectSettings
        ? state.projectSettings.filter(setting => setting.projectId !== viewSetting.projectId)
        : []

      if (viewSetting.projectId) {
        projectSettings.push(viewSetting)
      }

      return { projectSettings }
    })
  }

  /**
   * Updates a specific view setting in the store
   * @param {UserSetting} viewSettings
   * @param {string} targetedSetting
   */
  updateViewSetting(viewSettings: UserSetting, targetedSetting: string): void {
    this.update(state => {
      const matchedSetting: UserSetting[] = state.viewSettings[targetedSetting]

      if (matchedSetting && matchedSetting.length > 0) {
        const setting = matchedSetting.find(match => match.key === viewSettings.key)

        if (setting) {
          setting.value = viewSettings.value
        } else {
          matchedSetting.push(viewSettings)
        }
      } else {
        state.viewSettings[targetedSetting] = [viewSettings]
      }

      return state
    })
  }

  /**
   * Updates the task view setting in the store
   * @param {ITaskViewListsSetting} viewSetting
   */
  updateTaskViewListsSettings(viewSetting: ITaskViewListsSetting): void {
    this.update(state => {
      const taskViewListsSettings = state.taskViewListsSettings
        ? state.taskViewListsSettings.filter(setting => setting.id !== viewSetting.id)
        : []

      if (viewSetting.id) {
        taskViewListsSettings.push(viewSetting)
      }

      return { taskViewListsSettings }
    })
  }

  /**
   * Updates the dashboard task lists setting in the store
   * @param {ITaskViewListsSetting} viewSetting
   */
  updateTaskListWidgetSettings(viewSetting: ITaskViewListsSetting): void {
    this.update(state => {
      const taskListWidgetSettings = state.taskListWidgetSettings
        ? state.taskListWidgetSettings.filter(setting => setting.id !== viewSetting.id)
        : []

      if (viewSetting.id) {
        taskListWidgetSettings.push(viewSetting)
      }

      return { taskListWidgetSettings }
    })
  }

  /**
   * Update Grouping and Sorting
   * @param {IGroupingSorting} options
   */
  updateGroupingAndSortingOptions(options: IGroupingSorting): void {
    this.update({ sortingGrouping: options })
  }

  /**
   * Updates the entity times settings of a specific entity
   * @param {IEntityTimesSettings} viewSettings
   */
  updateEntityTimesSettings(viewSettings: IEntityTimesSettings): void {
    this.update(state => {
      const entityTimesSettings = state.entityTimesSettings
        ? state.entityTimesSettings.filter(
            setting =>
              !(
                setting.entityId === viewSettings.entityId &&
                setting.entityType === viewSettings.entityType &&
                setting.viewContext === viewSettings.viewContext
              )
          )
        : []

      entityTimesSettings.push(viewSettings)

      return { entityTimesSettings }
    })
  }

  /**
   * Updates the settings with the specified key
   * @param {K} key
   * @param {SettingsState[K]} value
   */
  updateSettings<K extends keyof SettingsState>(key: K, value: SettingsState[K]): void {
    this.update(storedSettings => {
      if (Array.isArray(storedSettings[key]) && Array.isArray(value)) {
        return {
          [key]: value
        }
      }

      if (typeof storedSettings[key] === 'object' && typeof value === 'object') {
        return {
          [key]: {
            ...storedSettings[key],
            ...value
          }
        }
      }

      return {
        [key]: value
      }
    })
  }

  /**
   * Updates the settings with the specified key, entity name and entity id
   * @param {EntitySettingsKey<K>} key
   * @param {EntityName} entityName
   * @param {string} entityId
   * @param {Partial<EntitySettingsValue<K>>} value
   * @template K
   */
  updateEntitySettings<K extends keyof SettingsState>(
    key: EntitySettingsKey<K>,
    entityName: EntityName,
    entityId: string,
    value: Partial<EntitySettingsValue<K>>
  ): void {
    this.update(storedSettings => {
      const entitySettingsArray = storedSettings[key] || []
      const existingSettingIndex = entitySettingsArray.findIndex(
        setting => setting.entityId === entityId && setting.entityName === entityName
      )

      if (existingSettingIndex !== -1) {
        // Update existing setting
        entitySettingsArray[existingSettingIndex] = { ...entitySettingsArray[existingSettingIndex], ...value }
      } else {
        // Add new setting
        entitySettingsArray.push({ entityName, entityId, ...value })
      }

      return { [key]: [...entitySettingsArray] }
    })
  }

  /**
   * Applies migrations to the view settings.
   * The migrations are applied to keep the view settings up to date with the current version of the app
   * @param {IViewPersistenceSetting} viewSetting
   */
  private applyMigrations(viewSetting: IViewPersistenceSetting): void {
    this.applyListColumnsMigration(viewSetting)
  }

  /**
   * Applies the migration for the list columns.
   * The migration takes the selected columns from the large list and moves them to the 'default' key
   * to use them as the default selected columns.
   * Remove this migration 6 months after the release of the custom fields.
   * @param {IViewPersistenceSetting} viewSetting
   */
  private applyListColumnsMigration(viewSetting: IViewPersistenceSetting): void {
    if (!viewSetting?.listColumns?.largeList?.projectTask) {
      return
    }

    if (viewSetting.listColumns.largeList.projectTask instanceof Array) {
      viewSetting.listColumns.largeList.projectTask = {
        default: viewSetting.listColumns.largeList.projectTask
      }
    }
  }
}
